import React, { useEffect, useState } from 'react';
import { mediaAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { X, Search, Image as ImageIcon, Check } from 'lucide-react';
import { resolveImageUrl } from '../../utils/imageUrl';

const MediaSelectorModal = ({ isOpen, onClose, onSelect, multiple = false }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadMedia();
      setSelected([]);
    }
  }, [isOpen]);

  const loadMedia = async () => {
    setLoading(true);
    try {
      const data = await mediaAPI.list();
      setMedia(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const getImageUrl = (item) => {
    if (item.url) return resolveImageUrl(item.url);
    if (item.path) return resolveImageUrl(item.path);
    if (item.filename) return resolveImageUrl(item.filename);
    return '';
  };

  const toggleSelect = (item) => {
    const url = getImageUrl(item);
    if (multiple) {
      setSelected(prev =>
        prev.includes(url) ? prev.filter(s => s !== url) : [...prev, url]
      );
    } else {
      onSelect(url);
      onClose();
    }
  };

  const confirmMultiple = () => {
    if (selected.length > 0) {
      onSelect(selected);
      onClose();
    }
  };

  const filtered = media.filter(item => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (item.filename || '').toLowerCase().includes(s) ||
           (item.alt || '').toLowerCase().includes(s) ||
           (item.folder || '').toLowerCase().includes(s);
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="media-selector-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-800">Biblioteca de Mídia</h2>
          <Button variant="ghost" size="sm" onClick={onClose}><X size={18} /></Button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              data-testid="media-search-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar imagens..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#055509]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ImageIcon size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nenhuma imagem encontrada</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {filtered.map((item, i) => {
                const url = getImageUrl(item);
                const isSelected = selected.includes(url);
                return (
                  <div
                    key={item.id || i}
                    data-testid={`media-item-${i}`}
                    className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all hover:shadow-md ${
                      isSelected ? 'border-[#055509] ring-2 ring-[#055509]/30' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => toggleSelect(item)}
                  >
                    <img src={url} alt={item.alt || item.filename} className="w-full h-24 object-cover" />
                    {isSelected && (
                      <div className="absolute top-1 right-1 bg-[#055509] text-white rounded-full w-5 h-5 flex items-center justify-center">
                        <Check size={12} />
                      </div>
                    )}
                    <div className="p-1">
                      <p className="text-[10px] text-gray-500 truncate">{item.filename || 'Imagem'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {multiple && (
          <div className="px-6 py-3 border-t flex justify-between items-center">
            <span className="text-sm text-gray-600">{selected.length} selecionada(s)</span>
            <Button
              data-testid="confirm-media-selection"
              onClick={confirmMultiple}
              className="bg-[#055509] hover:bg-[#0a7a0f] text-white"
              disabled={selected.length === 0}
            >
              Confirmar Seleção
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaSelectorModal;
